import { Coordinates, Dimensions, TreeDataNode } from "./tree-models";

export default class TreeNode {
  protected static readonly foldedClass = "tree-node-folded";
  protected static readonly htmlClass = "tree-node";

  protected htmlId: string;

  public branchWidth: number;
  public clientSize: Dimensions;
  public isHidden: boolean;
  public position: Coordinates;

  constructor(protected dataNode: TreeDataNode<unknown>) {
    this.htmlId = `${TreeNode.htmlClass}-${dataNode.id}`;

    this.branchWidth = 0;
    this.clientSize = { height: 0, width: 0 };
    this.position = { x: 0, y: 0 };
  }

  /**
   * create
   * Converts one input TreeDataNode to a nicely looking <div> element, ready to be displayed to the user.
   * The <div> will have "tree-node" class and "tree-node-XXX" ID for styling and other uses.
   *
   * By default, a node with some technical data is generated, but a descendant class of TreeNode could override
   * this function to generate a <div> with any content for various use cases.
   *
   * @param {TreeDataNode} node
   * @return {HTMLDivElement}
   */
  protected create(): HTMLDivElement {
    const nodeElement = document.createElement("div");

    // basic info about the node, for easier debugging
    nodeElement.innerHTML += `ID: ${this.dataNode.id}<br>`;
    nodeElement.innerHTML += `Parent: ${this.dataNode.parent?.id || "none"}<br>`;
    nodeElement.innerHTML += `Children: ${this.dataNode.children?.length || 0}<br>`;
    nodeElement.innerHTML += `Generation: ${this.dataNode.depth}<br>`;

    // generate some random text for testing purposes
    const randomText = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. ".split(" ");
    for (let i = 0, limit = Math.random() * randomText.length; i < limit; i++) {
      nodeElement.innerHTML += `${randomText[i]} `;
      if (Math.random() > 0.5) {
        nodeElement.innerHTML += "<br>";
      }
    }

    this.addStandardClasses(nodeElement);

    return nodeElement;
  }

  protected addStandardClasses(htmlNode: HTMLElement) {
    htmlNode.classList.add(TreeNode.htmlClass);
    if (this.dataNode.folded) {
      htmlNode.classList.add(TreeNode.foldedClass);
    }

    htmlNode.id = this.htmlId;
  }

  /**
   * Provides a standard way of getting the Node's dimensions. It can be overridden by the descendents as needed.
   *
   * @param {HTMLElement} htmlNode
   * @returns {height: number, width: number}
   */
  protected calculateClientSize(htmlNode: HTMLElement) {
    return { height: htmlNode.clientHeight, width: htmlNode.clientWidth };
  }

  /**
   * Moves the HTML node to the coordinates set in "this.position".
   * The node must have "position: absolute" applied, for this to work properly.
   *
   * @param {void}
   * @returns {void}
   */
  public translateToPosition(): void {
    const htmlNode = document.getElementById(this.htmlId);
    if (!htmlNode) {
      // if a node has been hidden in the config, we don't need to move it anywhere
      return;
    }
    htmlNode.style.transform = `translate3d(${this.position.x}px, ${this.position.y}px, 0)`;
  }

  /**
   * renderIn
   * Converts the input TreeDataNode to HTML and appends it to the given drawingContainer.
   * Then reads the node dimensions from the browser and stores them in "this.clientSize", so that
   * we can calculate the tree layout later.
   *
   * @param {HTMLElement} drawingContainer
   * @return {void}
   */
  public renderIn(drawingContainer: HTMLElement): void {
    if (this.isHidden) {
      return;
    }

    const htmlNode = this.create();

    drawingContainer.append(htmlNode);
    this.clientSize = this.calculateClientSize(htmlNode);
  }
}
